// DeltaFlowMeter — normalized full-chain Net Delta pressure gauge.
//
// Source:
// - Same chainSnaps / minuteFlows pipeline used by Flowmap.
// - Unlike Flowmap's bubbles, this widget does NOT restrict aggregation to the
//   +/-25 point bubble window. Every strike available in each flow snapshot is
//   included in the one-minute full-chain Net Delta observation.
//
// Score:
// - Rolling N-period Net Delta establishes direction.
// - Rolling gross absolute Net Delta establishes directional efficiency.
// - Gross activity is normalized against the session median for the same N.
// - Final score is bounded to [-100, +100].

import React, { useEffect, useId, useMemo, useState } from 'react';
import { WidgetPanelControls } from '../Dashboard/WidgetPanelSlot';
import { useOptionsStore } from '../../Store/optionsStore';
import { acquireBookmapSampler } from '../../Store/bookmapSampler';
import {
  isDuringMarketHours,
  latestTradeDayOnly,
} from '../../Utils/marketHours';
import { minuteFlows } from '../../Utils/strikeFlow';
import LiveBadge from '../Common/LiveBadge';
import '../../Styles/DeltaFlowMeter.css';

export const DELTA_FLOW_PERIOD_OPTIONS = [5, 10, 15, 20, 30];
export const DEFAULT_DELTA_FLOW_PERIOD = 10;

// Easy first-pass tuning points. These classify the normalized score, not raw
// Delta. We can move them after replaying a representative set of sessions.
export const DELTA_FLOW_NEUTRAL_THRESHOLD = 15;
export const DELTA_FLOW_STRONG_THRESHOLD = 45;

const ACTIVITY_RATIO_CAP = 2;
const TAPE_LENGTH = 48;
const PRESSURE_TREND_LOOKBACK = 4;
const PRESSURE_TREND_MIN_CHANGE = 4;

const GAUGE_CX = 200;
const GAUGE_CY = 192;
const GAUGE_OUTER_R = 184;
const GAUGE_INNER_R = 126;
const NEEDLE_R = 113;

// Exact Delta palette used by the chart. Palette order is weak -> strong.
export const DELTA_POSITIVE_PALETTE = [
  '#65528f',
  '#685ca3',
  '#6769b8',
  '#6078cb',
  '#5689dc',
  '#479be9',
  '#36adf2',
  '#25c0f5',
  '#19d3ef',
  '#16eadc',
];

export const DELTA_NEGATIVE_PALETTE = [
  '#fff45a',
  '#ffe63a',
  '#ffd922',
  '#ffca0b',
  '#ffb900',
  '#ffa000',
  '#ff8500',
  '#ff6800',
  '#f94c12',
  '#e85b16',
];

const DELTA_NEUTRAL_COLOR = '#252b31';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function median(values = []) {
  const clean = values
    .filter(Number.isFinite)
    .slice()
    .sort((a, b) => a - b);

  if (!clean.length) return 0;

  const mid = Math.floor(clean.length / 2);

  return clean.length % 2
    ? clean[mid]
    : (clean[mid - 1] + clean[mid]) / 2;
}

function polarPoint(cx, cy, radius, angleDeg) {
  const angle = (angleDeg * Math.PI) / 180;

  return {
    x: cx + (Math.cos(angle) * radius),
    y: cy + (Math.sin(angle) * radius),
  };
}

function annularSegmentPath(startDeg, endDeg) {
  const outerStart = polarPoint(
    GAUGE_CX,
    GAUGE_CY,
    GAUGE_OUTER_R,
    startDeg,
  );
  const outerEnd = polarPoint(
    GAUGE_CX,
    GAUGE_CY,
    GAUGE_OUTER_R,
    endDeg,
  );
  const innerEnd = polarPoint(
    GAUGE_CX,
    GAUGE_CY,
    GAUGE_INNER_R,
    endDeg,
  );
  const innerStart = polarPoint(
    GAUGE_CX,
    GAUGE_CY,
    GAUGE_INNER_R,
    startDeg,
  );

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${GAUGE_OUTER_R} ${GAUGE_OUTER_R} 0 0 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${GAUGE_INNER_R} ${GAUGE_INNER_R} 0 0 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function fullChainMinuteDelta(flowSnap) {
  let value = 0;
  let hasDelta = false;

  for (const cell of Object.values(flowSnap?.byStrike || {})) {
    const delta = Number(cell?.dDeltaNet);

    if (!Number.isFinite(delta)) continue;

    value += delta;
    hasDelta = true;
  }

  // null = this snapshot does not contain a Delta calculation yet.
  // 0    = Delta was calculated and the aggregate result is genuinely zero.
  //
  // minuteFlows() can briefly produce an empty trailing snapshot at the minute
  // boundary before the new chain diff is populated. Treating that as 0 is what
  // made the meter snap back to center.
  return hasDelta ? value : null;
}

export function buildFullChainDeltaSeries(flowSnaps = []) {
  return flowSnaps.map((snap, index) => {
    const value = fullChainMinuteDelta(snap);
    const isTrailingPending =
      index === flowSnaps.length - 1
      && !Number.isFinite(value);

    return {
      t: Number(snap?.t),

      // Keep a completed quiet historical minute as zero. Only the newest empty
      // minute is provisional and withheld until real Delta data arrives.
      value: isTrailingPending ? null : (value ?? 0),
      pending: isTrailingPending,

      resetWindow: Boolean(
        snap?.recoveryGap
        || snap?.hydrationSeam
        || snap?.hydrationBoundaryTail,
      ),
    };
  });
}

export function buildDeltaPressureSeries(minuteSeries = [], period = 10) {
  const safePeriod = Math.max(1, Number(period) || DEFAULT_DELTA_FLOW_PERIOD);
  const output = [];
  const segment = [];
  const completedGrossWindows = [];

  for (const point of minuteSeries) {
    if (!Number.isFinite(point?.t)) continue;

    // Hold the last completed reading while the newest minute is still empty.
    // This is not smoothing and does not change the score; it only prevents a
    // provisional no-data snapshot from being interpreted as real zero flow.
    if (point.pending || !Number.isFinite(point?.value)) {
      continue;
    }

    if (point.resetWindow) {
      segment.length = 0;
      output.push({
        ...point,
        score: null,
        net: null,
        gross: null,
        efficiency: null,
        activityRatio: null,
        sampleCount: 0,
        warmup: true,
      });
      continue;
    }

    segment.push(point);

    if (segment.length > safePeriod) {
      segment.shift();
    }

    const net = segment.reduce(
      (sum, item) => sum + (Number(item.value) || 0),
      0,
    );
    const gross = segment.reduce(
      (sum, item) => sum + Math.abs(Number(item.value) || 0),
      0,
    );

    const efficiency = gross > 0 ? net / gross : 0;
    const warmup = segment.length < safePeriod;

    const baselineGross = warmup
      ? gross
      : median([...completedGrossWindows, gross]);

    const activityRatio = baselineGross > 0
      ? gross / baselineGross
      : 0;

    // At normal session activity (ratio ~= 1), a perfectly one-sided window
    // scores ~50. Only unusually strong + one-sided flow can approach +/-100.
    const activityFactor = clamp(
      activityRatio / ACTIVITY_RATIO_CAP,
      0,
      1,
    );

    const score = clamp(
      efficiency * activityFactor * 100,
      -100,
      100,
    );

    output.push({
      ...point,
      score,
      net,
      gross,
      efficiency,
      activityRatio,
      sampleCount: segment.length,
      warmup,
    });

    if (!warmup) {
      completedGrossWindows.push(gross);
    }
  }

  return output;
}

function scoreState(score) {
  if (!Number.isFinite(score)) {
    return {
      key: 'neutral',
      label: 'Loading...',
    };
  }

  if (score <= -DELTA_FLOW_STRONG_THRESHOLD) {
    return {
      key: 'negative-strong',
      label: 'STRONG NEGATIVE',
    };
  }

  if (score <= -DELTA_FLOW_NEUTRAL_THRESHOLD) {
    return {
      key: 'negative-moderate',
      label: 'MODERATE NEGATIVE',
    };
  }

  if (score < DELTA_FLOW_NEUTRAL_THRESHOLD) {
    return {
      key: 'neutral',
      label: 'NEUTRAL',
    };
  }

  if (score < DELTA_FLOW_STRONG_THRESHOLD) {
    return {
      key: 'positive-moderate',
      label: 'MODERATE POSITIVE',
    };
  }

  return {
    key: 'positive-strong',
    label: 'STRONG POSITIVE',
  };
}

function fmtSigned(value, digits = 0) {
  if (!Number.isFinite(value)) return '--';

  const sign = value > 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(digits)}`;
}

function fmtCompact(value) {
  if (!Number.isFinite(value)) return '--';

  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  }

  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  }

  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(1)}k`;
  }

  return `${sign}${Math.round(abs)}`;
}

function fmtEtTime(timestamp) {
  if (!Number.isFinite(timestamp)) return '--:--';

  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '--:--';
  }
}

function pressureTrend(series = [], currentScore = null) {
  if (!Number.isFinite(currentScore)) return 'Loading...';
  if (Math.abs(currentScore) < DELTA_FLOW_NEUTRAL_THRESHOLD) return 'BALANCED';

  const scored = series.filter((point) => Number.isFinite(point?.score));
  if (scored.length < PRESSURE_TREND_LOOKBACK + 1) return 'DEVELOPING';

  const current = scored[scored.length - 1].score;
  const previous = scored[scored.length - 1 - PRESSURE_TREND_LOOKBACK].score;

  if (
    Math.sign(current) !== Math.sign(previous)
    && Math.abs(previous) >= DELTA_FLOW_NEUTRAL_THRESHOLD
  ) {
    return 'REVERSING';
  }

  const changeInMagnitude = Math.abs(current) - Math.abs(previous);

  if (changeInMagnitude >= PRESSURE_TREND_MIN_CHANGE) return 'BUILDING';
  if (changeInMagnitude <= -PRESSURE_TREND_MIN_CHANGE) return 'FADING';

  return 'STEADY';
}

function scoreSpectrumColor(score) {
  if (!Number.isFinite(score)) return DELTA_NEUTRAL_COLOR;

  const safe = clamp(score, -100, 100);
  const magnitude = Math.abs(safe);

  if (magnitude < DELTA_FLOW_NEUTRAL_THRESHOLD) {
    return DELTA_NEUTRAL_COLOR;
  }

  const palette = safe >= 0
    ? DELTA_POSITIVE_PALETTE
    : DELTA_NEGATIVE_PALETTE;

  const normalized = clamp(
    (magnitude - DELTA_FLOW_NEUTRAL_THRESHOLD)
      / (100 - DELTA_FLOW_NEUTRAL_THRESHOLD),
    0,
    1,
  );

  const index = Math.min(
    palette.length - 1,
    Math.round(normalized * (palette.length - 1)),
  );

  return palette[index];
}

function buildSpectrumStops() {
  const stops = [];

  // SVG linear-gradient offsets map directly to score:
  // -100 = 0%, 0 = 50%, +100 = 100%.
  const negativeSpanStart = 0;
  const negativeSpanEnd = (100 - DELTA_FLOW_NEUTRAL_THRESHOLD) / 200;
  const positiveSpanStart = (100 + DELTA_FLOW_NEUTRAL_THRESHOLD) / 200;
  const positiveSpanEnd = 1;

  // Left edge is strongest negative, so reverse the negative palette while
  // moving left -> center.
  DELTA_NEGATIVE_PALETTE
    .slice()
    .reverse()
    .forEach((color, index, values) => {
      const progress = values.length <= 1
        ? 0
        : index / (values.length - 1);

      stops.push({
        offset:
          negativeSpanStart
          + ((negativeSpanEnd - negativeSpanStart) * progress),
        color,
      });
    });

  // Deliberate neutral deadband in the middle.
  stops.push({
    offset: negativeSpanEnd + 0.006,
    color: DELTA_NEUTRAL_COLOR,
  });
  stops.push({
    offset: positiveSpanStart - 0.006,
    color: DELTA_NEUTRAL_COLOR,
  });

  DELTA_POSITIVE_PALETTE.forEach((color, index, values) => {
    const progress = values.length <= 1
      ? 0
      : index / (values.length - 1);

    stops.push({
      offset:
        positiveSpanStart
        + ((positiveSpanEnd - positiveSpanStart) * progress),
      color,
    });
  });

  return stops;
}

const DELTA_SPECTRUM_STOPS = buildSpectrumStops();

function scoreToNeedle(score) {
  const safeScore = clamp(
    Number.isFinite(score) ? score : 0,
    -100,
    100,
  );

  // -100 = 180 degrees (left), 0 = 270 (top), +100 = 360 (right).
  return 180 + (((safeScore + 100) / 200) * 180);
}

function needlePolygon(score) {
  const angle = scoreToNeedle(score);
  const tip = polarPoint(GAUGE_CX, GAUGE_CY, NEEDLE_R, angle);
  const leftBase = polarPoint(GAUGE_CX, GAUGE_CY, 7, angle - 90);
  const rightBase = polarPoint(GAUGE_CX, GAUGE_CY, 7, angle + 90);

  return [
    `${tip.x},${tip.y}`,
    `${leftBase.x},${leftBase.y}`,
    `${rightBase.x},${rightBase.y}`,
  ].join(' ');
}

function tapeClass(score) {
  return `dfm-tape-cell is-${scoreState(score).key}`;
}


export default function DeltaFlowMeter() {
  const rawSnaps = useOptionsStore(
    (state) => state.flowCalculationSnaps || state.chainSnaps || [],
  );
  const capture = useOptionsStore(
    (state) => state.captureChainSnapshot,
  );

  const [period, setPeriod] = useState(DEFAULT_DELTA_FLOW_PERIOD);
  const gradientId = useId().replace(/:/g, '');

  // Keep the same chain sampling pipeline alive even when Flowmap itself is not
  // the active widget.
  useEffect(
    () => acquireBookmapSampler(capture),
    [capture],
  );

  const snaps = useMemo(
    () => latestTradeDayOnly(
      rawSnaps.filter(
        (snap) => Number.isFinite(Number(snap?.t))
          && isDuringMarketHours(Number(snap.t)),
      ),
    ),
    [rawSnaps],
  );

  const flowSnaps = useMemo(
    () => minuteFlows(snaps),
    [snaps],
  );

  const minuteDelta = useMemo(
    () => buildFullChainDeltaSeries(flowSnaps),
    [flowSnaps],
  );

  const pressureSeries = useMemo(
    () => buildDeltaPressureSeries(minuteDelta, period),
    [minuteDelta, period],
  );

  const latest = pressureSeries.length
    ? pressureSeries[pressureSeries.length - 1]
    : null;
  const current = latest;

  const currentScore = Number.isFinite(current?.score)
    ? current.score
    : null;
  const state = scoreState(currentScore);
  const currentSpectrumColor = scoreSpectrumColor(currentScore);
  const tape = pressureSeries
    .filter((point) => Number.isFinite(point?.score))
    .slice(-TAPE_LENGTH);

  const pressureContext = pressureTrend(
    pressureSeries,
    currentScore,
  );

  const tapeStartTime = tape.length ? tape[0].t : null;

  return (
    <div className="dfm-panel">
      <WidgetPanelControls>
        <span
          className={`dfm-header-state is-${state.key}`}
          style={
            state.key === 'neutral'
              ? undefined
              : { color: currentSpectrumColor }
          }
        >
          {state.label}
        </span>

        <span className="dfm-header-score">
          {fmtSigned(currentScore, 0)}
        </span>

        <div
          className="dfm-period-control"
          role="group"
          aria-label="Delta Flow lookback window in minutes"
        >
          <span className="dfm-period-label">LOOKBACK</span>

          {DELTA_FLOW_PERIOD_OPTIONS.map((value) => (
            <button
              key={value}
              type="button"
              className={value === period ? 'is-active' : ''}
              aria-pressed={value === period}
              aria-label={`${value} minute lookback`}
              onClick={() => setPeriod(value)}
            >
              {value}m
            </button>
          ))}
        </div>

        <LiveBadge />
      </WidgetPanelControls>

      <div className="dfm-content">
        <div className="dfm-gauge-wrap">
          <svg
            className="dfm-gauge"
            viewBox="0 0 400 246"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Delta Flow score ${fmtSigned(currentScore, 0)}, ${state.label}`}
          >
            <defs>
              <linearGradient
                id={gradientId}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                {DELTA_SPECTRUM_STOPS.map((stop, index) => (
                  <stop
                    key={`${stop.offset}:${index}`}
                    offset={`${stop.offset * 100}%`}
                    stopColor={stop.color}
                  />
                ))}
              </linearGradient>
            </defs>

            <path
              className="dfm-spectrum-arc"
              d={annularSegmentPath(180, 360)}
              fill={`url(#${gradientId})`}
            />

            <text
              className="dfm-gauge-label is-negative"
              x="16"
              y="225"
              textAnchor="start"
            >
              NEGATIVE
            </text>
            <text
              className="dfm-gauge-label is-neutral"
              x="200"
              y="10"
              textAnchor="middle"
            >
              NEUTRAL
            </text>
            <text
              className="dfm-gauge-label is-positive"
              x="384"
              y="225"
              textAnchor="end"
            >
              POSITIVE
            </text>

            <polygon
              className={`dfm-needle is-${state.key}`}
              points={needlePolygon(currentScore)}
              style={{ fill: currentSpectrumColor }}
            />
            <circle
              className="dfm-pivot-outer"
              cx={GAUGE_CX}
              cy={GAUGE_CY}
              r="11"
            />
            <circle
              className="dfm-pivot-inner"
              cx={GAUGE_CX}
              cy={GAUGE_CY}
              r="5.5"
            />
          </svg>

          <div className="dfm-score-readout" aria-hidden="true">
            <span
              className={`dfm-score is-${state.key}`}
              style={
                state.key === 'neutral'
                  ? undefined
                  : { color: currentSpectrumColor }
              }
            >
              {fmtSigned(currentScore, 0)}
            </span>
            <span className="dfm-score-max">/ 100</span>
          </div>
        </div>

        <div className="dfm-primary-readout">
          <span
            className={`dfm-primary-state is-${state.key}`}
            style={
              state.key === 'neutral'
                ? undefined
                : { color: currentSpectrumColor }
            }
          >
            {state.label}
          </span>
          <span className={`dfm-pressure-context is-${pressureContext.toLowerCase()}`}>
            {pressureContext}
          </span>
        </div>

        <div className="dfm-summary">
          <span className="dfm-summary-stat">
            {period}m NET Δ <b>{fmtCompact(current?.net)}</b>
          </span>
          <span className="dfm-summary-stat">
            ALIGNMENT <b>{Number.isFinite(current?.efficiency)
              ? `${Math.round(Math.abs(current.efficiency) * 100)}%`
              : '--'}</b>
          </span>
          <span className="dfm-summary-stat">
            ACTIVITY <b>{Number.isFinite(current?.activityRatio)
              ? `${current.activityRatio.toFixed(2)}x`
              : '--'}</b>
          </span>
          {current?.warmup && (
            <span className="dfm-warmup">
              WARMUP {current.sampleCount}/{period}
            </span>
          )}
        </div>

        <div className="dfm-tape-block">
          <div className="dfm-tape-heading">
            <span className="dfm-tape-label">PRESSURE HISTORY</span>
            <span className="dfm-tape-window">{period}m NORMALIZED FLOW</span>
          </div>

          <div className="dfm-tape-row">
            <span className="dfm-tape-time">
              {fmtEtTime(tapeStartTime)}
            </span>

            <div
              className="dfm-tape"
              aria-label="Recent normalized Delta Flow pressure"
            >
              {tape.length ? tape.map((point, index) => (
                <span
                  key={`${point.t}:${index}`}
                  className={tapeClass(point.score)}
                  style={{
                    backgroundColor: scoreSpectrumColor(point.score),
                  }}
                  aria-label={`Delta Flow ${fmtEtTime(point.t)}`}
                  title={`${fmtEtTime(point.t)} • ${fmtSigned(point.score, 0)} • ${fmtCompact(point.net)}`}
                />
              )) : (
                <span className="dfm-tape-empty">Loading...</span>
              )}
            </div>

            <span className="dfm-tape-now">NOW</span>
          </div>
        </div>
      </div>
    </div>
  );
}
